import { useEffect, useState } from 'react';
import { X, ArrowRight, ArrowLeft, CheckCircle, Question } from '@phosphor-icons/react';
import { useLanguage } from '@/context/LanguageContext';

const STORAGE_PREFIX = 'growthriq_tour_seen_';

/**
 * OnboardingTour
 *
 * 3-4 adımlı, ilk ziyarette otomatik açılan modal. localStorage ile
 * "bir daha gösterme" durumunu kalıcılaştırır. <ReplayTourButton /> ile her
 * zaman yeniden tetiklenebilir.
 *
 * Props:
 * - tourId: localStorage key (örn: "geo", "competitor")
 * - steps: [{ title, body, icon? }]
 */
export default function ToolTour({ tourId, steps }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(() => {
    if (typeof window === 'undefined') return false;
    return !localStorage.getItem(STORAGE_PREFIX + tourId);
  });
  const [step, setStep] = useState(0);
  const [dontShow, setDontShow] = useState(true);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = () => {
      setStep(0);
      setOpen(true);
    };
    window.addEventListener(`growthriq:tour:${tourId}`, handler);
    return () => window.removeEventListener(`growthriq:tour:${tourId}`, handler);
  }, [tourId]);

  if (!open || !steps?.length) return null;

  const close = () => {
    if (dontShow) {
      localStorage.setItem(STORAGE_PREFIX + tourId, '1');
    }
    setOpen(false);
  };

  const isLast = step === steps.length - 1;
  const current = steps[step];
  const Icon = current.icon || CheckCircle;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      data-testid={`tour-${tourId}-modal`}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={close} />

      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden text-[#0F1B36]">
        {/* Close */}
        <button
          onClick={close}
          aria-label="close"
          data-testid={`tour-${tourId}-close`}
          className="absolute top-3 right-3 p-2 rounded-lg hover:bg-black/5 text-[#0F1B36]/60 hover:text-[#0F1B36] transition z-10"
        >
          <X size={18} weight="bold" />
        </button>

        {/* Top accent bar (progress) */}
        <div className="h-1 bg-[#F1F5F9]">
          <div
            className="h-full bg-[#3CD9B8] transition-all duration-300"
            style={{ width: `${((step + 1) / steps.length) * 100}%` }}
          />
        </div>

        <div className="px-7 pt-8 pb-6">
          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#3CD9B8]/15 text-[#0F1B36]">
              <Icon size={20} weight="duotone" />
            </span>
            <span className="text-xs font-semibold uppercase tracking-wider text-[#0F1B36]/50">
              {t('tour.step')} {step + 1} / {steps.length}
            </span>
          </div>

          <h3
            className="text-2xl font-bold tracking-tight mb-3"
            data-testid={`tour-${tourId}-title`}
          >
            {current.title}
          </h3>
          <p className="text-[#0F1B36]/70 leading-relaxed text-[15px] whitespace-pre-line">
            {current.body}
          </p>
        </div>

        {/* Footer */}
        <div className="px-7 pb-6 pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-[#0F1B36]/10">
          <label className="flex items-center gap-2 text-xs text-[#0F1B36]/60 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={dontShow}
              onChange={(e) => setDontShow(e.target.checked)}
              className="accent-[#3CD9B8]"
              data-testid={`tour-${tourId}-dont-show`}
            />
            {t('tour.dontShowAgain')}
          </label>

          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg hover:bg-black/5 text-[#0F1B36]/80"
                data-testid={`tour-${tourId}-prev`}
              >
                <ArrowLeft size={14} weight="bold" /> {t('tour.prev')}
              </button>
            )}
            {!isLast ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-[#0F1B36] text-white hover:opacity-90"
                data-testid={`tour-${tourId}-next`}
              >
                {t('tour.next')} <ArrowRight size={14} weight="bold" />
              </button>
            ) : (
              <button
                onClick={close}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-[#3CD9B8] text-[#0F1B36] hover:opacity-90"
                data-testid={`tour-${tourId}-finish`}
              >
                {t('tour.finish')} <CheckCircle size={14} weight="bold" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Replays an onboarding tour via a custom event. Place anywhere on the
 * tool page (e.g. header). Renders a small "Tanıtım" button.
 */
export function ReplayTourButton({ tourId, className = '' }) {
  const { t } = useLanguage();
  return (
    <button
      type="button"
      onClick={() => window.dispatchEvent(new CustomEvent(`growthriq:tour:${tourId}`))}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-white/15 text-white/80 hover:text-white hover:border-white/30 transition ${className}`}
      data-testid={`tour-${tourId}-replay`}
    >
      <Question size={14} weight="bold" />
      {t('tour.replay')}
    </button>
  );
}
